import React from 'react';
import { useStepEditorContext } from 'src/store/step-editor';

import { v4 as uuidv4 } from 'uuid';
import { isValidUrl, isValidMailtoUrl } from 'src/utils/custom-validations';
import {
  STEP_EDITOR_CONTENT_OPTION_URL,
  CONTENT_TYPE_EMBED,
  CONTENT_TYPE_EXTERNAL_LINK,
  CONTENT_BEHAVIOR_EXTERNAL_LINK,
  CONTENT_TYPE_VIDEO,
} from 'src/editor/constants';
import { isVideoUrl } from 'src/utils/url';

import { apiGetEmbedDetailsFromUrl } from 'src/utils/journeyApi';
import { ErrorInterface } from 'src/common/interfaces/error.interface';

export const useEmbedUrlChange = (params: { overwriteLink?: boolean } = { overwriteLink: true }) => {
  const {
    url: existingUrl,
    name,
    contentName,
    link,
    setName,
    setContentName,
    setDestination,
    setUrl,
    setLink,
    setLoading,
    setContentType,
    setContentOption,
    setContentBehavior,
    setIsEmbeddable,
    setWarningMessage,
    setTransformedUrl,
    setEmbedCode,
    setUploadedS3Asset,
    setCheckEmbedUrlFromLibrary,
  } = useStepEditorContext();

  const updateMetadata = (response: any, is_link = false) => {
    const { meta_data, destination } = response;
    if (meta_data && params.overwriteLink) {
      const { title } = meta_data;
      if (title) {
        if (!name) {
          setName(title);
        }
        if (!contentName) {
          setContentName(title);
        }
      }
      let linkCopy = link;

      if (meta_data.title) {
        linkCopy = {
          ...linkCopy,
          title: meta_data.title,
        };
      }

      if (meta_data.description) {
        linkCopy = {
          ...linkCopy,
          description: meta_data.description,
        };
      }

      if (meta_data.thumbnail?.url) {
        linkCopy = {
          ...linkCopy,
          meta_image_url: meta_data.thumbnail.url,
        };
      } else if (meta_data.icon?.url) {
        linkCopy = {
          ...linkCopy,
          meta_image_url: meta_data.icon.url,
        };
      }

      if (destination) {
        setDestination(destination);
      }

      setLink(linkCopy);
    }
  };

  const processEmbedDetailsResponse = (response: any, url: string, isLibrary = false) => {
    const { title } = response.meta_data;
    if (title) {
      if (!name) {
        setName(title);
      }
      if (!contentName) {
        setContentName(title);
      }
    }

    setCheckEmbedUrlFromLibrary(isLibrary);

    if (isVideoUrl(url)) {
      setUploadedS3Asset({
        video_uuid: uuidv4().replace(/-/g, ''),
        video_name: url.substring(0, 100),
        url: url,
        content_type: 'video/mp4',
      });
      setContentType(CONTENT_TYPE_VIDEO);
      setIsEmbeddable(true);
      updateMetadata(response);
      return Promise.resolve(response);
    } else if (response.transformed_url && response.iframe_safe) {
      setUrl(url);
      setTransformedUrl(response.transformed_url);
      setContentType(CONTENT_TYPE_EMBED);
      setIsEmbeddable(true);
      updateMetadata(response);
      return Promise.resolve(response);
    } else if (response.embed_code) {
      setUrl(url);
      setContentType(CONTENT_TYPE_EMBED);
      setIsEmbeddable(true);
      updateMetadata(response);
      setEmbedCode(response.embed_code);
      return Promise.resolve(response);
    } else if (response.error) {
      setContentType(CONTENT_TYPE_EXTERNAL_LINK);
      setIsEmbeddable(false);
      updateMetadata(response, true);
      setContentBehavior(CONTENT_BEHAVIOR_EXTERNAL_LINK);
      return Promise.reject({
        message: `This URL does not load: either the URL is incorrect or permissions are wrong. Please confirm in an incognito browser window. Otherwise, you can still add this url as a link.`,
      });
    } else if (response.third_party_iframe_safe) {
      setUrl(url);
      setTransformedUrl(response.transformed_url);
      setContentType(CONTENT_TYPE_EMBED);
      setIsEmbeddable(true);
      updateMetadata(response);
      setEmbedCode(response.embed_code);
      return Promise.resolve(response);
    } else {
      setUrl(url);
      setContentType(CONTENT_TYPE_EXTERNAL_LINK);
      setIsEmbeddable(false);
      updateMetadata(response, true);
      setContentBehavior(CONTENT_BEHAVIOR_EXTERNAL_LINK);
      return Promise.reject({
        message: `This content is being shown as a link as it may not embed well within a Journey.`,
      });
    }
  };

  const handleEmailTo = (url: string) => {
    setName('Email me');
    setContentType(CONTENT_TYPE_EXTERNAL_LINK);
    setIsEmbeddable(false);
    setContentBehavior(CONTENT_BEHAVIOR_EXTERNAL_LINK);
  };

  const checkEmbedUrl = async (url: string, options?: { isLibrary?: boolean; forceCheck?: boolean }) => {
    const isMailto = isValidMailtoUrl(url);

    if (options?.forceCheck || (url !== existingUrl && (isMailto || isValidUrl(url)))) {
      setLoading(true);
      setUrl(url);
      setContentOption(STEP_EDITOR_CONTENT_OPTION_URL);
      if (isMailto) {
        handleEmailTo(url);
        setLoading(false);
      } else {
        try {
          const response: any = await apiGetEmbedDetailsFromUrl(url);
          if (response.transformed_url.includes('notion-proxy')) {
            response.iframe_safe = false;
          }
          await processEmbedDetailsResponse(response, url, options?.isLibrary);
          setLoading(false);
          return response;
        } catch (e) {
          const message = (e as ErrorInterface).message || 'Received an error, please check the url and try again.';
          setWarningMessage(message);
          setLoading(false);
        }
      }
    }
    return { ignore: true };
  };

  return {
    checkEmbedUrl,
  };
};
